import React, { useCallback, useEffect, useState } from "react";
import type { ClientBirthdayRow } from "../../../types";
import { clientsApi } from "../../../services";
import {
  formatDateForDisplay,
  formatDateShort,
  getBrazilCalendarMonthBounds,
  getBrazilCalendarWeekBounds,
  getBrazilTodayBounds,
  getTodayMonthYearBrazil,
} from "../../../utils/dateUtils";
import { AdminPageHeader } from "../shared";
import { Card } from "../../shared";
import { Icons } from "../../shared/Icons";
import { ClientBirthdaysPanel } from "./ClientBirthdaysPanel";

function clientsWithBirthdayDates(list: ClientBirthdayRow[]): ClientBirthdayRow[] {
  return list.filter((c) => Boolean(c.birthday && String(c.birthday).trim()));
}

interface RangeState {
  clients: ClientBirthdayRow[];
  loading: boolean;
  error: string | null;
}

const emptyRange = (): RangeState => ({
  clients: [],
  loading: true,
  error: null,
});

export const AdminClientBirthdaysPage: React.FC = () => {
  const [today, setToday] = useState<RangeState>(() => emptyRange());
  const [week, setWeek] = useState<RangeState>(() => emptyRange());
  const [month, setMonth] = useState<RangeState>(() => emptyRange());

  const loadAll = useCallback(async () => {
    const t = getBrazilTodayBounds();
    const w = getBrazilCalendarWeekBounds();
    const { year, month: m } = getTodayMonthYearBrazil();
    const mo = getBrazilCalendarMonthBounds(year, m);

    setToday((s) => ({ ...s, loading: true, error: null }));
    setWeek((s) => ({ ...s, loading: true, error: null }));
    setMonth((s) => ({ ...s, loading: true, error: null }));

    const fetchRange = async (
      start: string,
      end: string,
      set: React.Dispatch<React.SetStateAction<RangeState>>,
    ) => {
      try {
        const res = await clientsApi.getClientBirthdays({ start, end });
        if (res.success && res.data) {
          set({ clients: res.data, loading: false, error: null });
        } else {
          set({
            clients: [],
            loading: false,
            error: res.error || "Erro ao carregar aniversários",
          });
        }
      } catch (e) {
        set({
          clients: [],
          loading: false,
          error: e instanceof Error ? e.message : "Erro ao carregar aniversários",
        });
      }
    };

    await Promise.all([
      fetchRange(t.startDate, t.endDate, setToday),
      fetchRange(w.startDate, w.endDate, setWeek),
      fetchRange(mo.startDate, mo.endDate, setMonth),
    ]);
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const tBounds = getBrazilTodayBounds();
  const wBounds = getBrazilCalendarWeekBounds();
  const { year, month: monthNum } = getTodayMonthYearBrazil();
  const mBounds = getBrazilCalendarMonthBounds(year, monthNum);

  const allLoaded = !today.loading && !week.loading && !month.loading;
  const noErrors = !today.error && !week.error && !month.error;
  const noBirthdaysAnywhere =
    allLoaded &&
    noErrors &&
    clientsWithBirthdayDates(today.clients).length === 0 &&
    clientsWithBirthdayDates(week.clients).length === 0 &&
    clientsWithBirthdayDates(month.clients).length === 0;

  return (
    <div className="space-y-4 sm:space-y-6 md:space-y-8 animate-fade-in">
      <AdminPageHeader
        icon={<Icons.Cake sx={{ fontSize: 28 }} />}
        title="Aniversários"
        subtitle="Clientes com aniversário hoje, na semana atual e no mês atual."
      />

      {noBirthdaysAnywhere ? (
        <Card className="p-10 sm:p-14 border border-gray-100 shadow-sm">
          <div className="flex flex-col items-center text-center max-w-md mx-auto">
            <Icons.Cake sx={{ fontSize: 56 }} className="text-gray-300" />
            <h2 className="mt-4 text-xl font-semibold text-gray-900">
              Nenhum aniversário no período
            </h2>
            <p className="mt-2 text-gray-600 text-sm sm:text-base leading-relaxed">
              Não há clientes com data de nascimento cadastrada para hoje, para esta
              semana ou para este mês. Cadastre a data de nascimento no perfil do
              cliente para aparecer aqui.
            </p>
          </div>
        </Card>
      ) : (
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
        <ClientBirthdaysPanel
          title="Hoje"
          subtitle="Aniversários neste dia"
          rangeLabel={`${formatDateForDisplay(tBounds.startDate)}`}
          clients={today.clients}
          loading={today.loading}
          error={today.error}
          onRetry={loadAll}
        />
        <ClientBirthdaysPanel
          title="Esta semana"
          subtitle="Segunda a domingo da semana corrente"
          rangeLabel={`${formatDateShort(wBounds.startDate)} — ${formatDateShort(wBounds.endDate)}`}
          clients={week.clients}
          loading={week.loading}
          error={week.error}
          onRetry={loadAll}
        />
        <ClientBirthdaysPanel
          title="Este mês"
          subtitle="Mês atual"
          rangeLabel={`${formatDateShort(mBounds.startDate)} — ${formatDateShort(mBounds.endDate)}`}
          clients={month.clients}
          loading={month.loading}
          error={month.error}
          onRetry={loadAll}
        />
      </div>
      )}
    </div>
  );
};
